/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import { Modal, Button, Table } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import validationErrorMsg from '../helpers/ValidationErrorMsg'
import Spinner from 'react-bootstrap/Spinner'

export interface AdvanceAdjustmentItem {
    idSalesPayments: number
    adjustmentAmount: number
}

export interface AdvancePaymentAdjustmentModalProps {
    show: boolean
    onClose: () => void
    idCompanies: number
    idProjects: number
    maxAdjustableAmount: number
    totalInvoiceAmount: number
    vatAmount?: number
    discountAmount?: number
    /** When provided, modal is in "edit" mode - submits to API. When null, "create" mode - calls onConfirm only */
    idSalesInvoice?: number | null
    /** Called when user confirms in create mode - receives the advance adjustments array */
    onConfirm?: (advanceAdjustments: AdvanceAdjustmentItem[]) => void
    /** Called after successful API submit in edit mode */
    onSuccess?: () => void
}

const numberFormat = (value: any) => {
    value = parseFloat(value)
    if (typeof value !== 'number') return value
    return parseFloat(value.toString()).toLocaleString('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
}

const AdvancePaymentAdjustmentModal: React.FC<AdvancePaymentAdjustmentModalProps> = ({
    show,
    onClose,
    idCompanies,
    idProjects,
    maxAdjustableAmount,
    totalInvoiceAmount,
    vatAmount = 0,
    discountAmount = 0,
    idSalesInvoice = null,
    onConfirm,
    onSuccess,
}) => {
    const [advancePayments, setAdvancePayments] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const getEffectiveMaxAdjustable = () => {
        const total = totalInvoiceAmount + (parseFloat(String(vatAmount)) || 0) - (parseFloat(String(discountAmount)) || 0)
        if (total < maxAdjustableAmount) return total
        return maxAdjustableAmount
    }

    const fetchAdvancePayments = () => {
        if (!idCompanies || !idProjects) return
        setLoading(true)
        fetch(
            process.env.REACT_APP_API_URL + `sales-payment/advance-by-client?idCompanies=${idCompanies}&idProjects=${idProjects}`,
            getRequestOptions()
        )
            .then((resp) => resp.json())
            .then((resp) => {
                const payments = (resp.data ?? []).map((p: any) => ({
                    ...p,
                    adjustmentAmount: 0,
                }))
                setAdvancePayments(payments)
            })
            .catch((error) => console.log(error, 'fetchAdvancePayments'))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        if (show && idCompanies && idProjects) {
            fetchAdvancePayments()
        }
    }, [show, idCompanies, idProjects])

    const handleAdjustmentAmountChange = (paymentId: number, value: number) => {
        const effectiveMax = getEffectiveMaxAdjustable()

        setAdvancePayments((prev: any[]) => {
            const otherTotal = prev.reduce(
                (sum: number, p: any) => (p.id !== paymentId ? sum + (parseFloat(p.adjustmentAmount) || 0) : sum),
                0
            )
            const remaining = parseFloat(prev.find((p: any) => p.id === paymentId)?.remainingAmount) || 0

            if (value > remaining) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid Amount',
                    text: `Adjustment cannot exceed remaining amount (${numberFormat(remaining)})`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                })
                return prev
            }

            if (otherTotal + value > effectiveMax) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid Amount',
                    text: `Total adjustment cannot exceed max adjustable amount (${numberFormat(effectiveMax)})`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                })
                return prev
            }

            return prev.map((p: any) => (p.id === paymentId ? { ...p, adjustmentAmount: value } : p))
        })
    }

    const handleConfirm = () => {
        const totalAdjustment = advancePayments.reduce(
            (sum: number, p: any) => sum + (parseFloat(p.adjustmentAmount) || 0),
            0
        )
        const effectiveMax = getEffectiveMaxAdjustable()
        if (totalAdjustment > effectiveMax) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Amount',
                text: `Total adjustment (${numberFormat(totalAdjustment)}) cannot exceed max adjustable amount (${numberFormat(effectiveMax)})`,
            })
            return
        }

        const advanceAdjustments: AdvanceAdjustmentItem[] = advancePayments
            .filter((p: any) => parseFloat(p.adjustmentAmount) > 0)
            .map((p: any) => ({
                idSalesPayments: p.id,
                adjustmentAmount: parseFloat(p.adjustmentAmount),
            }))

        if (advanceAdjustments.length === 0 && totalAdjustment === 0) {
            onClose()
            if (idSalesInvoice && onSuccess) onSuccess()
            return
        }

        // Create mode: no idSalesInvoice, just call onConfirm
        if (!idSalesInvoice) {
            if (onConfirm) onConfirm(advanceAdjustments)
            onClose()
            return
        }

        // Edit mode: call API
        setSubmitting(true)
        const payload: any = {
            idSalesInvoice,
            adjustmentAmount: totalAdjustment,
        }
        if (advanceAdjustments.length > 0) {
            payload.advanceAdjustments = advanceAdjustments
        }

        fetch(`${process.env.REACT_APP_API_URL}sales-invoice/make-adjustment`, postRequestOptions(payload))
            .then((resp) => resp.json())
            .then((respData) => {
                if (respData.success === true) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: respData.successMessage ?? respData.message ?? 'Adjustment saved successfully.',
                    })
                    onClose()
                    onSuccess?.()
                } else {
                    const errors = respData.errors ?? (respData.message ? { message: [respData.message] } : undefined)
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        html: validationErrorMsg(errors).join('<br>'),
                    })
                }
            })
            .catch((error) => {
                console.log(error, 'makeAdjustment')
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save adjustment.' })
            })
            .finally(() => setSubmitting(false))
    }

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Advance Payment Adjustment</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <small className="text-muted d-block mb-2">Max Adjustable Amount: {numberFormat(getEffectiveMaxAdjustable())}</small>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" size="sm" /> Loading advance payments...
                    </div>
                ) : (
                    <Table size="sm" bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: '5%' }} className="text-center">
                                    #
                                </th>
                                <th style={{ width: '15%' }}>Payment Date</th>
                                <th style={{ width: '18%' }} className="text-end">
                                    Advance Amount
                                </th>
                                <th style={{ width: '18%' }} className="text-end">
                                    Already Adjusted
                                </th>
                                <th style={{ width: '18%' }} className="text-end">
                                    Remaining Amount
                                </th>
                                <th style={{ width: '20%' }} className="text-end">
                                    Adjustment Amount
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {advancePayments.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted">
                                        No advance payments found for this client
                                    </td>
                                </tr>
                            )}
                            {advancePayments.map((payment: any, index: number) => (
                                <tr key={payment.id ?? index}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>{payment.paymentDate}</td>
                                    <td className="text-end">{numberFormat(payment.paymentAmount)}</td>
                                    <td className="text-end">{numberFormat(payment.alreadyAdjusted ?? 0)}</td>
                                    <td className="text-end">{numberFormat(payment.remainingAmount ?? 0)}</td>
                                    <td>
                                        <input
                                            type="number"
                                            step="any"
                                            min={0}
                                            max={payment.remainingAmount ?? 0}
                                            className="form-control form-control-sm text-end"
                                            value={payment.adjustmentAmount ?? ''}
                                            onChange={(e) =>
                                                handleAdjustmentAmountChange(payment.id, parseFloat(e.target.value) || 0)
                                            }
                                        />
                                    </td>
                                </tr>
                            ))}
                            {advancePayments.length > 0 && (
                                <tr className="table-light fw-bold">
                                    <td colSpan={2} className="text-end">
                                        Total
                                    </td>
                                    <td className="text-end">
                                        {numberFormat(
                                            advancePayments.reduce(
                                                (sum: number, p: any) => sum + (parseFloat(p.paymentAmount) || 0),
                                                0
                                            )
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {numberFormat(
                                            advancePayments.reduce(
                                                (sum: number, p: any) => sum + (parseFloat(p.alreadyAdjusted) || 0),
                                                0
                                            )
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {numberFormat(
                                            advancePayments.reduce(
                                                (sum: number, p: any) => sum + (parseFloat(p.remainingAmount) || 0),
                                                0
                                            )
                                        )}
                                    </td>
                                    <td className="text-end">
                                        {numberFormat(
                                            advancePayments.reduce(
                                                (sum: number, p: any) => sum + (parseFloat(p.adjustmentAmount) || 0),
                                                0
                                            )
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" size="sm" onClick={handleConfirm} disabled={submitting || loading}>
                    {submitting && (
                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" className="me-1" />
                    )}
                    Confirm
                </Button>
                <Button variant="secondary" size="sm" onClick={onClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AdvancePaymentAdjustmentModal
